import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { useLeague } from "./LeagueLayout";

const MILESTONE_CATALOG = [
  { code: "T0", label: "Rose Iniziali", desc: "Post asta iniziale — snapshot rose partenza stagione", color: "cyan" },
  { code: "T1", label: "Pre Riparazione", desc: "Prima dell'asta di riparazione (gennaio) — dopo aggiornamento listone", color: "amber" },
  { code: "T2", label: "Post Riparazione", desc: "Dopo l'asta di riparazione — nuove rose e crediti", color: "emerald" },
  { code: "T3", label: "Fine Campionato", desc: "Ultimo listone finale + rose stagione conclusa", color: "rose" },
  { code: "T4", label: "Pre Asta Furti", desc: "Luglio — post svincoli volontari, pronti per asta", color: "purple" },
  { code: "T5", label: "Post Asta Furti", desc: "Dopo l'asta furti — chiusura anno / apertura successivo", color: "indigo" },
];

const COLOR_MAP = {
  cyan: "border-cyan-500/40 bg-cyan-500/10 text-cyan-300",
  amber: "border-amber-500/40 bg-amber-500/10 text-amber-300",
  emerald: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  rose: "border-rose-500/40 bg-rose-500/10 text-rose-300",
  purple: "border-purple-500/40 bg-purple-500/10 text-purple-300",
  indigo: "border-indigo-500/40 bg-indigo-500/10 text-indigo-300",
  slate: "border-slate-700 bg-slate-800/40 text-slate-300",
};

export default function LeagueMilestones() {
  const { league, isAdmin } = useLeague();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [selectedCode, setSelectedCode] = useState("T0");
  const [customLabel, setCustomLabel] = useState("");
  const [detail, setDetail] = useState(null);
  const [diff, setDiff] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/leagues/${league.league_id}/milestones`);
      setItems(r.data.milestones || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [league.league_id]);

  const createMilestone = async () => {
    const catalog = MILESTONE_CATALOG.find(c => c.code === selectedCode);
    const label = customLabel.trim() || `${catalog.label} — ${league.season || ""}`;
    setCreating(true);
    try {
      const r = await api.post(`/leagues/${league.league_id}/milestones`, {
        code: selectedCode, label, season: league.season,
      });
      toast.success(`✅ Milestone ${r.data.code} creata (${r.data.players_count} players · ${r.data.teams_count} teams)`);
      setCustomLabel("");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCreating(false); }
  };

  const removeMilestone = async (mid) => {
    if (!confirm(`Elimina milestone ${mid}? Lo snapshot verrà perso.`)) return;
    try {
      await api.delete(`/leagues/${league.league_id}/milestones/${mid}`);
      toast.success("Milestone eliminata");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openDetail = async (mid) => {
    try {
      const r = await api.get(`/leagues/${league.league_id}/milestones/${mid}`);
      setDetail(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openDiff = async (mid) => {
    setDiffLoading(true);
    try {
      const r = await api.get(`/leagues/${league.league_id}/milestones/${mid}/diff`);
      if (!r.data.has_prev) {
        toast.info("Nessuna milestone precedente per il confronto");
        return;
      }
      setDiff(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDiffLoading(false); }
  };

  if (!isAdmin) {
    return <div className="text-slate-500 py-10 text-center">Solo l&apos;admin può accedere alle Milestones.</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs tracking-[0.3em] text-cyan-400 uppercase font-bold">🏁 Season Milestones</div>
        <h2 className="text-3xl font-black tracking-tight">Timeline stagione</h2>
        <p className="text-slate-400 text-sm mt-1">Punti di riferimento navigabili: Rose Iniziali · Pre/Post Riparazione · Fine Campionato · Pre/Post Asta Furti. Ogni milestone salva l&apos;intera fotografia (players + teams + budgets).</p>
      </div>

      {/* Create panel */}
      <div className="rounded-3xl border border-slate-800 bg-gradient-to-br from-slate-900 to-slate-950 p-5 space-y-3">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500">➕ Crea nuova milestone</div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
          {MILESTONE_CATALOG.map(c => (
            <button key={c.code} onClick={() => setSelectedCode(c.code)} data-testid={`milestone-code-${c.code}`}
              className={`text-left p-3 rounded-2xl border-2 transition-all ${selectedCode === c.code ? "border-cyan-400 ring-2 ring-cyan-400/40 bg-cyan-500/10" : "border-slate-800 bg-slate-900/40 hover:border-slate-600"}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-xs px-2 py-0.5 rounded-full font-mono font-black border ${COLOR_MAP[c.color]}`}>{c.code}</span>
                <span className="font-bold text-slate-100">{c.label}</span>
              </div>
              <div className="text-[11px] text-slate-400">{c.desc}</div>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input value={customLabel} onChange={e => setCustomLabel(e.target.value)} placeholder="Etichetta custom (opzionale)" data-testid="milestone-custom-label"
            className="flex-1 min-w-[220px] bg-slate-950 border border-slate-800 rounded-2xl px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600"/>
          <button onClick={createMilestone} disabled={creating} className="px-5 py-2 rounded-2xl bg-cyan-500/20 border border-cyan-500/40 text-cyan-200 font-black text-sm hover:bg-cyan-500/30 disabled:opacity-50" data-testid="milestone-create-btn">
            {creating ? "..." : `📸 Crea ${selectedCode}`}
          </button>
        </div>
      </div>

      {/* Timeline */}
      {loading ? (
        <div className="text-slate-500 py-10 text-center">Caricamento…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-slate-800 rounded-3xl p-10 text-center text-slate-500">
          <div className="text-4xl mb-2">📸</div>
          Nessuna milestone salvata. Crea la prima per iniziare a costruire la timeline.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map(m => {
            const cat = MILESTONE_CATALOG.find(c => c.code === m.code) || { color: "slate" };
            return (
              <div key={m.milestone_id} className={`p-4 rounded-3xl border ${COLOR_MAP[cat.color]} border-l-4`} data-testid={`milestone-row-${m.milestone_id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono font-black text-xs px-2 py-0.5 rounded-full bg-black/30 border border-white/10">{m.code}</span>
                      <h3 className="text-lg font-black text-slate-100">{m.label}</h3>
                      {m.season && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-800 text-slate-400 font-mono">stagione {m.season}</span>}
                    </div>
                    <div className="text-xs text-slate-400 mt-1">
                      {m.players_count} giocatori · {m.teams_count} squadre · fase {m.phase ?? "?"} · {new Date(m.created_at).toLocaleString("it-IT")}
                    </div>
                    {m.note && <div className="text-xs text-slate-500 italic mt-1">📝 {m.note}</div>}
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => openDiff(m.milestone_id)} disabled={diffLoading} data-testid={`milestone-diff-${m.milestone_id}`}
                      className="text-xs px-3 py-1.5 rounded-full bg-purple-500/15 hover:bg-purple-500/30 text-purple-300 border border-purple-500/30 font-bold disabled:opacity-50">🔀 Diff</button>
                    <button onClick={() => openDetail(m.milestone_id)} data-testid={`milestone-detail-${m.milestone_id}`}
                      className="text-xs px-3 py-1.5 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold">👁️ Dettaglio</button>
                    <button onClick={() => removeMilestone(m.milestone_id)} data-testid={`milestone-delete-${m.milestone_id}`}
                      className="text-xs px-3 py-1.5 rounded-full bg-rose-500/15 hover:bg-rose-500/30 text-rose-300 border border-rose-500/30 font-bold">🗑️</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div onClick={e => e.stopPropagation()} className="w-full max-w-4xl max-h-[85vh] rounded-3xl bg-slate-900 border border-cyan-500/40 flex flex-col" data-testid="milestone-detail-modal">
            <div className="p-5 border-b border-slate-800">
              <div className="text-xs text-cyan-400 font-bold uppercase tracking-widest">Milestone {detail.code}</div>
              <h3 className="text-2xl font-black">{detail.label}</h3>
              <div className="text-xs text-slate-400 mt-1">{detail.players_count} giocatori · {detail.teams_count} squadre · {new Date(detail.created_at).toLocaleString("it-IT")}</div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 grid grid-cols-1 md:grid-cols-2 gap-2">
              {(detail.teams_snapshot || []).map(t => (
                <div key={t.team_id} className="p-3 rounded-2xl bg-slate-950 border border-slate-800">
                  <div className="font-bold text-slate-100">{t.name}</div>
                  <div className="text-xs text-slate-400 mt-0.5">Budget: <strong className="text-emerald-300">{t.budget}cr</strong></div>
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    Giocatori: {(detail.players_snapshot || []).filter(p => p.team_id === t.team_id).length}
                  </div>
                </div>
              ))}
            </div>
            <div className="p-3 border-t border-slate-800 flex justify-end">
              <button onClick={() => setDetail(null)} className="px-4 py-2 rounded-2xl bg-slate-800 text-slate-300 text-sm font-bold">Chiudi</button>
            </div>
          </div>
        </div>
      )}

      {/* Iter 64: Diff Modal */}
      {diff && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4" onClick={() => setDiff(null)}>
          <div onClick={e => e.stopPropagation()} className="w-full max-w-5xl max-h-[90vh] rounded-3xl bg-slate-900 border border-purple-500/40 flex flex-col" data-testid="milestone-diff-modal">
            <div className="p-5 border-b border-slate-800">
              <div className="text-xs text-purple-400 font-bold uppercase tracking-widest">🔀 Confronto Milestones</div>
              <h3 className="text-2xl font-black">{diff.current.label} <span className="text-slate-500 font-normal text-lg">vs</span> {diff.previous.label}</h3>
              <div className="text-xs text-slate-400 mt-1">
                <span className="text-purple-300 font-mono">{diff.previous.code}</span> ({new Date(diff.previous.created_at).toLocaleString("it-IT")}) → <span className="text-purple-300 font-mono">{diff.current.code}</span> ({new Date(diff.current.created_at).toLocaleString("it-IT")})
              </div>
              <div className="flex gap-4 mt-2 text-xs">
                <span className="text-emerald-300">➕ {diff.summary.players_added} giocatori nuovi</span>
                <span className="text-rose-300">➖ {diff.summary.players_removed} rimossi</span>
                <span className="text-slate-400">🏆 {diff.summary.teams_count} squadre</span>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {diff.teams_diff.map(t => {
                const bdelta = t.budget_delta;
                const bColor = bdelta > 0 ? "text-emerald-300" : bdelta < 0 ? "text-rose-300" : "text-slate-400";
                return (
                  <div key={t.team_id} className="rounded-2xl border border-slate-800 bg-slate-950/50 p-4" data-testid={`diff-team-${t.team_id}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                      <div className="font-bold text-slate-100">{t.team_name}</div>
                      <div className="flex items-center gap-3 text-xs font-mono">
                        <span className="text-slate-500">Budget: {t.budget_prev}cr → <strong className="text-slate-200">{t.budget_curr}cr</strong></span>
                        <span className={`font-black ${bColor}`}>{bdelta > 0 ? "+" : ""}{bdelta}cr</span>
                        <span className="text-slate-500">Rosa: {t.roster_prev} → {t.roster_curr}</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                      {t.players_gained_sample.length > 0 && (
                        <div className="p-2 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
                          <div className="font-bold text-emerald-300 mb-1">➕ Arrivati ({t.players_gained_count})</div>
                          <ul className="space-y-0.5 text-emerald-200">
                            {t.players_gained_sample.map(p => (
                              <li key={p.player_id}>• {p.name} <span className="text-emerald-500/70 text-[10px]">{p.prezzo_pagato}cr</span></li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {t.players_lost_sample.length > 0 && (
                        <div className="p-2 rounded-xl bg-rose-500/10 border border-rose-500/30">
                          <div className="font-bold text-rose-300 mb-1">➖ Persi ({t.players_lost_count})</div>
                          <ul className="space-y-0.5 text-rose-200">
                            {t.players_lost_sample.map(p => (
                              <li key={p.player_id}>• {p.name} <span className="text-rose-500/70 text-[10px]">{p.prezzo_pagato}cr</span></li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {t.transferred_in_sample.length > 0 && (
                        <div className="p-2 rounded-xl bg-cyan-500/10 border border-cyan-500/30">
                          <div className="font-bold text-cyan-300 mb-1">🔁 Scambi in ({t.transferred_in_count})</div>
                          <ul className="space-y-0.5 text-cyan-200">
                            {t.transferred_in_sample.map(p => <li key={p.player_id}>• {p.name}</li>)}
                          </ul>
                        </div>
                      )}
                      {t.transferred_out_sample.length > 0 && (
                        <div className="p-2 rounded-xl bg-amber-500/10 border border-amber-500/30">
                          <div className="font-bold text-amber-300 mb-1">🔁 Scambi out ({t.transferred_out_count})</div>
                          <ul className="space-y-0.5 text-amber-200">
                            {t.transferred_out_sample.map(p => <li key={p.player_id}>• {p.name}</li>)}
                          </ul>
                        </div>
                      )}
                      {t.players_gained_count === 0 && t.players_lost_count === 0 && t.transferred_in_count === 0 && t.transferred_out_count === 0 && (
                        <div className="col-span-2 text-slate-500 text-xs italic p-2">Nessuna variazione della rosa.</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="p-3 border-t border-slate-800 flex justify-end">
              <button onClick={() => setDiff(null)} className="px-4 py-2 rounded-2xl bg-slate-800 text-slate-300 text-sm font-bold" data-testid="diff-close-btn">Chiudi</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
